import { memo } from 'react';
import { motion } from 'framer-motion';
import type { CityState } from '@/utils/cityVisuals';

interface CitySilhouetteProps {
  cityState: CityState;
}

/**
 * SVG city silhouette that responds to fatigue level
 * Includes buildings, windows, spire, bridge, and lanterns
 */
export const CitySilhouette = memo(function CitySilhouette({ cityState }: CitySilhouetteProps) {
  const { windowsLit, lanternGlow, buildingVisibility } = cityState;

  // Generate random windows that are lit
  const getWindowOpacity = (index: number) => {
    // Deterministic "random" based on index
    const seed = (index * 7 + 3) % 10 / 10;
    return seed < windowsLit ? windowsLit : 0.05;
  };

  return (
    <div
      className="city-silhouette-container"
      style={{ opacity: 0.3 + buildingVisibility * 0.7 }}
    >
      <svg
        viewBox="0 0 400 80"
        preserveAspectRatio="xMidYMax slice"
        className="w-full h-full"
        style={{ filter: `brightness(${0.5 + buildingVisibility * 0.5})` }}
      >
        <defs>
          {/* Gradient for buildings */}
          <linearGradient id="buildingGrad" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#1A1D33" />
            <stop offset="100%" stopColor="#0D0F1A" />
          </linearGradient>

          {/* Lantern glow filter */}
          <filter id="lanternGlow" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="2" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        {/* Bridge on the left */}
        <path
          d="M0 70 Q25 60 50 70 L50 80 L0 80 Z"
          fill="url(#buildingGrad)"
        />
        <path
          d="M10 68 Q25 62 40 68"
          stroke="#2D1B4E"
          strokeWidth="1"
          fill="none"
          opacity={buildingVisibility}
        />

        {/* Building 1 - Short */}
        <rect x="55" y="50" width="25" height="30" fill="url(#buildingGrad)" />
        <rect x="60" y="55" width="4" height="5" fill={`rgba(245, 230, 204, ${getWindowOpacity(1)})`} />
        <rect x="67" y="55" width="4" height="5" fill={`rgba(232, 168, 124, ${getWindowOpacity(2)})`} />
        <rect x="60" y="63" width="4" height="5" fill={`rgba(232, 168, 124, ${getWindowOpacity(3)})`} />
        <rect x="67" y="63" width="4" height="5" fill={`rgba(245, 230, 204, ${getWindowOpacity(4)})`} />

        {/* Building 2 - Tall with dome */}
        <rect x="85" y="30" width="30" height="50" fill="url(#buildingGrad)" />
        <ellipse cx="100" cy="30" rx="12" ry="6" fill="#1A1D33" />
        <rect x="90" y="38" width="5" height="6" fill={`rgba(245, 230, 204, ${getWindowOpacity(5)})`} />
        <rect x="98" y="38" width="5" height="6" fill={`rgba(232, 168, 124, ${getWindowOpacity(6)})`} />
        <rect x="106" y="38" width="5" height="6" fill={`rgba(245, 230, 204, ${getWindowOpacity(7)})`} />
        <rect x="90" y="50" width="5" height="6" fill={`rgba(232, 168, 124, ${getWindowOpacity(8)})`} />
        <rect x="98" y="50" width="5" height="6" fill={`rgba(245, 230, 204, ${getWindowOpacity(9)})`} />
        <rect x="106" y="50" width="5" height="6" fill={`rgba(232, 168, 124, ${getWindowOpacity(10)})`} />
        <rect x="90" y="62" width="5" height="6" fill={`rgba(245, 230, 204, ${getWindowOpacity(11)})`} />
        <rect x="98" y="62" width="5" height="6" fill={`rgba(232, 168, 124, ${getWindowOpacity(12)})`} />
        <rect x="106" y="62" width="5" height="6" fill={`rgba(245, 230, 204, ${getWindowOpacity(13)})`} />

        {/* Building 3 - Medium */}
        <rect x="120" y="45" width="22" height="35" fill="url(#buildingGrad)" />
        <rect x="125" y="52" width="4" height="5" fill={`rgba(232, 168, 124, ${getWindowOpacity(14)})`} />
        <rect x="133" y="52" width="4" height="5" fill={`rgba(245, 230, 204, ${getWindowOpacity(15)})`} />
        <rect x="125" y="62" width="4" height="5" fill={`rgba(245, 230, 204, ${getWindowOpacity(16)})`} />
        <rect x="133" y="62" width="4" height="5" fill={`rgba(232, 168, 124, ${getWindowOpacity(17)})`} />

        {/* Building 4 - Church with spire (Petersburg reference) */}
        <rect x="150" y="35" width="35" height="45" fill="url(#buildingGrad)" />
        <polygon points="167.5,10 155,35 180,35" fill="#1A1D33" />
        <line x1="167.5" y1="5" x2="167.5" y2="15" stroke="#9B8EC4" strokeWidth="1" opacity={buildingVisibility} />
        <rect x="156" y="42" width="5" height="6" fill={`rgba(245, 230, 204, ${getWindowOpacity(18)})`} />
        <rect x="165" y="42" width="5" height="6" fill={`rgba(232, 168, 124, ${getWindowOpacity(19)})`} />
        <rect x="174" y="42" width="5" height="6" fill={`rgba(245, 230, 204, ${getWindowOpacity(20)})`} />
        <rect x="156" y="55" width="5" height="6" fill={`rgba(232, 168, 124, ${getWindowOpacity(21)})`} />
        <rect x="165" y="55" width="5" height="6" fill={`rgba(245, 230, 204, ${getWindowOpacity(22)})`} />
        <rect x="174" y="55" width="5" height="6" fill={`rgba(232, 168, 124, ${getWindowOpacity(23)})`} />
        {/* Main church window */}
        <ellipse cx="167.5" cy="68" rx="6" ry="8" fill={`rgba(155, 142, 196, ${getWindowOpacity(24) * 1.2})`} />

        {/* Building 5 - Short */}
        <rect x="190" y="55" width="20" height="25" fill="url(#buildingGrad)" />
        <rect x="195" y="60" width="4" height="5" fill={`rgba(232, 168, 124, ${getWindowOpacity(25)})`} />
        <rect x="202" y="60" width="4" height="5" fill={`rgba(245, 230, 204, ${getWindowOpacity(26)})`} />

        {/* Building 6 - Tall modern */}
        <rect x="215" y="25" width="25" height="55" fill="url(#buildingGrad)" />
        <rect x="220" y="32" width="4" height="5" fill={`rgba(245, 230, 204, ${getWindowOpacity(27)})`} />
        <rect x="228" y="32" width="4" height="5" fill={`rgba(232, 168, 124, ${getWindowOpacity(28)})`} />
        <rect x="220" y="42" width="4" height="5" fill={`rgba(232, 168, 124, ${getWindowOpacity(29)})`} />
        <rect x="228" y="42" width="4" height="5" fill={`rgba(245, 230, 204, ${getWindowOpacity(30)})`} />
        <rect x="220" y="52" width="4" height="5" fill={`rgba(245, 230, 204, ${getWindowOpacity(31)})`} />
        <rect x="228" y="52" width="4" height="5" fill={`rgba(232, 168, 124, ${getWindowOpacity(32)})`} />
        <rect x="220" y="62" width="4" height="5" fill={`rgba(232, 168, 124, ${getWindowOpacity(33)})`} />
        <rect x="228" y="62" width="4" height="5" fill={`rgba(245, 230, 204, ${getWindowOpacity(34)})`} />

        {/* Building 7 - Medium with peaked roof */}
        <rect x="245" y="40" width="28" height="40" fill="url(#buildingGrad)" />
        <polygon points="259,30 245,40 273,40" fill="#1A1D33" />
        <rect x="251" y="48" width="5" height="6" fill={`rgba(245, 230, 204, ${getWindowOpacity(35)})`} />
        <rect x="260" y="48" width="5" height="6" fill={`rgba(232, 168, 124, ${getWindowOpacity(36)})`} />
        <rect x="251" y="60" width="5" height="6" fill={`rgba(232, 168, 124, ${getWindowOpacity(37)})`} />
        <rect x="260" y="60" width="5" height="6" fill={`rgba(245, 230, 204, ${getWindowOpacity(38)})`} />

        {/* Building 8 - Short end */}
        <rect x="278" y="52" width="22" height="28" fill="url(#buildingGrad)" />
        <rect x="283" y="58" width="4" height="5" fill={`rgba(245, 230, 204, ${getWindowOpacity(39)})`} />
        <rect x="291" y="58" width="4" height="5" fill={`rgba(232, 168, 124, ${getWindowOpacity(40)})`} />

        {/* Building 9 - End building */}
        <rect x="305" y="45" width="25" height="35" fill="url(#buildingGrad)" />
        <rect x="310" y="52" width="4" height="5" fill={`rgba(232, 168, 124, ${getWindowOpacity(41)})`} />
        <rect x="318" y="52" width="4" height="5" fill={`rgba(245, 230, 204, ${getWindowOpacity(42)})`} />
        <rect x="310" y="62" width="4" height="5" fill={`rgba(245, 230, 204, ${getWindowOpacity(43)})`} />
        <rect x="318" y="62" width="4" height="5" fill={`rgba(232, 168, 124, ${getWindowOpacity(44)})`} />

        {/* Bridge on the right */}
        <path
          d="M340 70 Q365 58 390 70 L390 80 L340 80 Z"
          fill="url(#buildingGrad)"
        />
        <path
          d="M350 68 Q365 60 380 68"
          stroke="#2D1B4E"
          strokeWidth="1"
          fill="none"
          opacity={buildingVisibility}
        />

        {/* End building */}
        <rect x="375" y="55" width="25" height="25" fill="url(#buildingGrad)" />
        <rect x="380" y="60" width="4" height="5" fill={`rgba(232, 168, 124, ${getWindowOpacity(45)})`} />
        <rect x="388" y="60" width="4" height="5" fill={`rgba(245, 230, 204, ${getWindowOpacity(46)})`} />

        {/* Lanterns */}
        {lanternGlow > 0 && (
          <g filter="url(#lanternGlow)">
            <motion.circle
              cx="75" cy="72" r="2"
              fill="#E8A87C"
              opacity={lanternGlow}
              animate={{ opacity: [lanternGlow * 0.8, lanternGlow, lanternGlow * 0.8] }}
              transition={{ duration: 2, repeat: Infinity }}
            />
            <motion.circle
              cx="145" cy="72" r="2"
              fill="#E8A87C"
              opacity={lanternGlow}
              animate={{ opacity: [lanternGlow, lanternGlow * 0.8, lanternGlow] }}
              transition={{ duration: 2.5, repeat: Infinity }}
            />
            <motion.circle
              cx="210" cy="72" r="2"
              fill="#E8A87C"
              opacity={lanternGlow}
              animate={{ opacity: [lanternGlow * 0.9, lanternGlow, lanternGlow * 0.9] }}
              transition={{ duration: 1.8, repeat: Infinity }}
            />
            <motion.circle
              cx="300" cy="72" r="2"
              fill="#E8A87C"
              opacity={lanternGlow}
              animate={{ opacity: [lanternGlow * 0.85, lanternGlow, lanternGlow * 0.85] }}
              transition={{ duration: 2.2, repeat: Infinity }}
            />
            <motion.circle
              cx="365" cy="72" r="2"
              fill="#E8A87C"
              opacity={lanternGlow}
              animate={{ opacity: [lanternGlow, lanternGlow * 0.9, lanternGlow] }}
              transition={{ duration: 2, repeat: Infinity }}
            />
          </g>
        )}
      </svg>
    </div>
  );
});
